import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useUser } from '../../contexts/UserContext'
import { API_CONFIG } from '../../config/apiConfig'
import { clearAllUserCachedData, retryJSAPIOnInvalidSession } from '../../utils/auth_access_util'
import axios from 'axios'
import './index.css'

/**
 * G14: 账号绑定状态页
 *
 * 展示用户飞书账号与系统账号的绑定状态，包括：
 * - 绑定状态卡片（已绑定/未绑定/已解绑）
 * - 使用条件列表
 * - 未匹配原因说明
 * - 重新绑定按钮（已解绑时显示）
 * - "联系管理员"按钮
 */
export default function BindAccount() {
    const navigate = useNavigate()
    const { userInfo, loading, isBound, refreshUserInfoLight } = useUser()
    const [bindStatus, setBindStatus] = useState({
        isBound: false,
        accountInfo: null,
        unmatchedReason: null
    })
    const [rebinding, setRebinding] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [unbinding, setUnbinding] = useState(false)
    const [toastMsg, setToastMsg] = useState(null)

    const showToast = (msg) => {
        setToastMsg(msg)
        setTimeout(() => setToastMsg(null), 2500)
    }

    useEffect(() => {
        if (!loading && userInfo) {
            // 判断绑定状态
            const bound = !!(userInfo.open_id && userInfo.open_id.length > 0)
            let reason = null
            if (!bound) {
                reason = userInfo.unmatched_reason || '您的飞书账号尚未与系统账号关联，请联系企业管理员进行配置。'
            }
            setBindStatus({
                isBound: bound,
                accountInfo: {
                    openId: userInfo.open_id || '未知',
                    companyName: userInfo.company_name || API_CONFIG.companyName || '',
                    userName: userInfo.name || '未知用户'
                },
                unmatchedReason: reason
            })
        }
    }, [loading, userInfo])

    const handleContactAdmin = () => {
        // 优先使用 adminOpenId，如果为空则尝试从 adminOpenIds 数组取第一个
        let adminOpenId = API_CONFIG.adminOpenId
        if (!adminOpenId && API_CONFIG.adminOpenIds) {
            try {
                const arr = JSON.parse(API_CONFIG.adminOpenIds)
                if (Array.isArray(arr) && arr.length > 0) {
                    adminOpenId = arr[0]
                }
            } catch (e) {
                // adminOpenIds 不是 JSON 数组，尝试按逗号/换行分隔取第一个
                const first = API_CONFIG.adminOpenIds.split(/[\n,]/)[0]?.trim()
                if (first) adminOpenId = first
            }
        }
        console.log('[联系管理员] adminOpenId =', adminOpenId, 'window.tt =', typeof window.tt, 'window.tt.enterChat =', typeof window.tt?.enterChat)
        
        // 检查是否配置了管理员
        if (!adminOpenId) {
            console.warn('[联系管理员] adminOpenId 未配置')
            alert('管理员联系方式未配置，请联系企业管理员或在管理后台配置。客服电话：400-9638-868')
            return
        }
        
        // 检查飞书环境
        if (typeof window === 'undefined' || !window.tt) {
            console.warn('[联系管理员] 非飞书环境')
            alert('请在飞书客户端内打开此功能。客服电话：400-9638-868')
            return
        }
        
        if (window.tt.enterChat) {
            // 用 Promise 包装 enterChat 的 success/fail，便于与重试逻辑配合
            const callEnterChat = () => new Promise((resolve, reject) => {
                window.tt.enterChat({
                    openid: adminOpenId,
                    success: (res) => {
                        console.log('打开管理员聊天窗口成功', res)
                        resolve(res)
                    },
                    fail: (err) => {
                        console.error('打开管理员聊天窗口失败:', err)
                        reject(err)
                    }
                })
            })

            retryJSAPIOnInvalidSession(callEnterChat)
                .then(() => {
                    // 成功：飞书已打开聊天窗口，不需额外提示
                })
                .catch((err) => {
                    // 重试后仍失败：把错误提示给用户
                    console.error('打开管理员聊天窗口最终失败:', err)
                    const errMsg = (err && (err.errMsg || err.msg)) || '请检查管理员是否在当前企业内'
                    // 如果是 invalid session 且重试已用完，给出明确指引
                    const hint = /invalid session/.test(errMsg)
                        ? '\n\n提示：飞书客户端会话已过期，请下拉刷新页面重试。'
                        : ''
                    alert('打开聊天窗口失败: ' + errMsg + hint + '\n客服电话：400-9638-868')
                })
        } else {
            alert('当前飞书环境不支持打开聊天窗口，请升级飞书版本或联系客服。电话：400-9638-868')
        }
    }

    /**
     * 重新绑定：清除Redis中的解绑标记，刷新页面后自动重新绑定
     */
    const handleRebind = async () => {
        setRebinding(true)
        try {
            // 从解绑前保存的数据中获取 tenantKey 和 openId
            const preUnbindStr = localStorage.getItem('preUnbindData')
            let targetTenantKey = ''
            let targetOpenId = ''

            if (preUnbindStr) {
                try {
                    const preUnbindData = JSON.parse(preUnbindStr)
                    targetTenantKey = preUnbindData.tenantKey || ''
                    targetOpenId = preUnbindData.openId || ''
                } catch (e) {
                    console.warn('[BindAccount] 解析解绑前数据失败:', e)
                }
            }

            // 如果 local 没有，从 API_CONFIG 获取
            if (!targetTenantKey) {
                targetTenantKey = userInfo?.tenant_key || API_CONFIG.tenantKey || ''
            }

            if (!targetTenantKey) {
                showToast('无法获取企业信息，请刷新页面后重试')
                setRebinding(false)
                return
            }

            // 调用重新绑定接口
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/feishu/user/rebind`, {
                tenantKey: targetTenantKey,
                openId: targetOpenId
            }, {
                headers: { 'Content-Type': 'application/json' }
            })

            if (response.data && response.data.code === 200) {
                showToast('重新绑定成功，正在刷新...')
                // ★ 清除所有缓存（用户、权限、租户配置、access token），防止旧数据残留
                clearAllUserCachedData('isAdmin')
                clearAllUserCachedData('isDriver')
                clearAllUserCachedData('isDirectDispatch')
                clearAllUserCachedData('userPermissions')
                clearAllUserCachedData('tenantConfig')
                clearAllUserCachedData('userInfo')
                localStorage.removeItem('userInfo')
                localStorage.removeItem('lk_token')
                localStorage.removeItem('preUnbindData')
                // 强制完全刷新（不用 React state 重置，让整个页面重建）
                setTimeout(() => {
                    window.location.href = window.location.origin + window.location.pathname
                }, 1500)
            } else {
                showToast(response.data?.message || '重新绑定失败，请稍后重试')
            }
        } catch (error) {
            console.error('[BindAccount] 重新绑定请求失败:', error)
            showToast('网络异常，请稍后重试')
        } finally {
            setRebinding(false)
        }
    }

    /**
     * 解绑操作
     */
    const handleUnbind = () => {
        setShowConfirm(true)
    }

    const handleCancelUnbind = () => {
        setShowConfirm(false)
    }

    const handleConfirmUnbind = async () => {
        setShowConfirm(false)
        setUnbinding(true)
        try {
            const tenantKey = userInfo?.tenant_key || API_CONFIG.tenantKey || ''
            const openId = userInfo?.open_id || ''
            if (!openId) {
                showToast('未找到绑定信息')
                setUnbinding(false)
                return
            }
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/feishu/user/unbind`, {
                tenantKey,
                openId
            }, {
                headers: { 'Content-Type': 'application/json' }
            })
            if (response.data?.code === 200) {
                showToast('解绑成功')
                try {
                    localStorage.setItem('preUnbindData', JSON.stringify({ tenantKey, openId }))
                } catch (e) {
                    // ignore storage error
                }
                // ★ 清空所有用户相关缓存（权限、用户信息、access token 等）
                clearAllUserCachedData('userInfo')
                clearAllUserCachedData('isAdmin')
                clearAllUserCachedData('isDriver')
                clearAllUserCachedData('isDirectDispatch')
                clearAllUserCachedData('userPermissions')
                clearAllUserCachedData('tenantConfig')
                localStorage.removeItem('userInfo')
                localStorage.removeItem('lk_token')
                setTimeout(() => {
                    navigate('/')
                    window.location.reload()
                }, 1500)
            } else {
                showToast(response.data?.message || '解绑失败，请稍后重试')
            }
        } catch (error) {
            showToast('网络异常，请稍后重试')
        } finally {
            setUnbinding(false)
        }
    }

    const [refreshing, setRefreshing] = useState(false);
    const handleRefreshStatus = async () => {
        setRefreshing(true);
        try {
            // 强制刷新权限：绕过缓存重新查询，保证后台权限变更即时生效
            await refreshUserInfoLight(true);
            showToast('用户信息已刷新');
        } catch (e) {
            showToast('刷新失败，请重试');
        } finally {
            setRefreshing(false);
        }
    }

    if (loading) {
        return (
            <div className="bind-account-page">
                <div className="bind-loading">
                    <div className="loading-spinner" />
                    <p>加载中...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="bind-account-page">
            <div className="bind-header">
                <h2>账号设置</h2>
            </div>

            {/* 状态卡片 */}
            <div className={`bind-status-card ${bindStatus.isBound ? 'bound' : 'unbound'}`}>
                <div className="status-icon">
                    {bindStatus.isBound ? '✅' : '⚠️'}
                </div>
                <div className="status-text">
                    {bindStatus.isBound ? '已绑定' : '未绑定'}
                </div>
                {bindStatus.accountInfo && bindStatus.isBound && (
                    <div className="account-details">
                        <div className="detail-row">
                            <span className="detail-label">飞书账号</span>
                            <span className="detail-value">{bindStatus.accountInfo.openId}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">绑定企业</span>
                            <span className="detail-value">{bindStatus.accountInfo.companyName}</span>
                        </div>
                        {bindStatus.accountInfo.userName && (
                            <div className="detail-row">
                                <span className="detail-label">用户名称</span>
                                <span className="detail-value">{bindStatus.accountInfo.userName}</span>
                            </div>
                        )}
                        {userInfo?.mobile && (
                            <div className="detail-row">
                                <span className="detail-label">电话号码</span>
                                <span className="detail-value">{(userInfo.mobile || '').replace(/^\+86/, '')}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 使用条件 */}
            <div className="bind-condition-card">
                <h3>使用条件说明</h3>
                <ul className="condition-list">
                    <li className="condition-item">
                        <span className="condition-bullet">•</span>
                        <span>管理员功能：需后台配置管理员权限</span>
                    </li>
                    <li className="condition-item">
                        <span className="condition-bullet">•</span>
                        <span>司机功能：需后台配置司机权限</span>
                    </li>
                    <li className="condition-item">
                        <span className="condition-bullet">•</span>
                        <span>用车申请：全员可用（需完成飞书配置）</span>
                    </li>
                    <li className="condition-item">
                        <span className="condition-bullet">•</span>
                        <span>预约记录：全员可用</span>
                    </li>
                </ul>
            </div>

            {/* 未匹配原因 */}
            {!bindStatus.isBound && bindStatus.unmatchedReason && (
                <div className="bind-reason-card">
                    <h3>未匹配原因</h3>
                    <p className="reason-text">{bindStatus.unmatchedReason}</p>
                </div>
            )}

            {/* 操作按钮 */}
            <div className="bind-actions">
                {/* 已绑定时显示解除绑定按钮 */}
                {bindStatus.isBound && (
                    <button
                        className="action-btn unbind-btn"
                        onClick={handleUnbind}
                        disabled={unbinding}
                    >
                        {unbinding ? '解绑中...' : '解除绑定'}
                    </button>
                )}
                {/* 已解绑时显示重新绑定按钮 */}
                {!bindStatus.isBound && (
                    <button
                        className="action-btn rebind-btn"
                        onClick={handleRebind}
                        disabled={rebinding}
                    >
                        {rebinding ? '重新绑定中...' : '重新绑定'}
                    </button>
                )}
                <button className="action-btn contact-btn" onClick={handleContactAdmin}>
                    联系管理员
                </button>
                <button className="action-btn refresh-btn" onClick={handleRefreshStatus} disabled={refreshing}>
                    {refreshing ? '刷新中...' : '刷新用户信息'}
                </button>
                <button className="action-btn back-btn" onClick={() => navigate('/')}>
                    返回首页
                </button>
            </div>

            {/* 二次确认弹窗 */}
            {showConfirm && (
                <div className="confirm-overlay" onClick={handleCancelUnbind}>
                    <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-title">确认解绑</div>
                        <div className="confirm-body">
                            解绑后您将无法使用用车申请等功能，需要重新授权才能恢复。确定要解除绑定吗？
                        </div>
                        <div className="confirm-actions">
                            <button className="confirm-cancel" onClick={handleCancelUnbind}>取消</button>
                            <button className="confirm-ok" onClick={handleConfirmUnbind}>确认解绑</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast提示 */}
            {toastMsg && (
                <div className="toast-message">{toastMsg}</div>
            )}
        </div>
    )
}
